// ============================================================================
// CANCUN100 — MiPanel.js  (Bloque 28: "Mi panel" básico del agente)
// ----------------------------------------------------------------------------
// Foto de perfil (cambiar), tema claro/oscuro y color de acento (opciones predefinidas) → agentes.preferencias
// {"tema":"oscuro","acento":"turquesa"}; abajo "Mi ficha · v{n} · enviada el <fecha>" en SOLO LECTURA y en gris (Bloque 29):
// el agente no puede editarla; si necesita corregir algo, se lo pide a administración (que la reabre).
// Nada más por ahora.
// ============================================================================
import React, { useState, useEffect, useRef } from 'react';
import { RADIO, RADIO_TARJETA, RADIO_AVATAR } from './tema';
import { Ic } from './iconos'; // APP 2/24
import { MENUS, etiquetaMenu, migrarMenu, IconoMenu } from './menuApp'; // APP 2/09 (F8): menú arriba / lateral
import ModalError from './ModalError';
import { guardarFotoPerfil, guardarPreferencias, urlFirmada, validarArchivo } from './ficha';
import FichaLectura from './FichaLectura'; // Bloque 29

export default function MiPanel({ C, agente, tema, onCambiarTema, onActualizarAgente }) {
  const [foto, setFoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errorModal, setErrorModal] = useState(null);
  const fileRef = useRef(null);
  const prefs = (agente && agente.preferencias) || {};
  // APP 2/14 (F10): sin skins (agentes.preferencias.skin se queda en la base y se ignora). Solo claro/oscuro y el menú.
  const menu = migrarMenu(prefs.menu); // APP 2/09 (F8): 'arriba' (default) | 'lateral'

  useEffect(() => {
    let vivo = true;
    (async () => { try { const u = await urlFirmada(agente && agente.foto_url, 3600); if (vivo) setFoto(u); } catch { if (vivo) setFoto(null); } })();
    return () => { vivo = false; };
  }, [agente]);

  const cambiarFoto = async (file) => {
    const e = validarArchivo(file);
    if (e) { setErrorModal({ titulo: 'Esa foto no sirve', mensaje: e }); return; }
    setBusy(true);
    try {
      const ruta = await guardarFotoPerfil(agente.id, file);
      try { setFoto(URL.createObjectURL(file)); } catch { /* sin vista */ }
      if (onActualizarAgente) onActualizarAgente({ foto_url: ruta });
    } catch (err) {
      setErrorModal({ titulo: 'No se pudo subir la foto', mensaje: 'Intenta de nuevo.', detalle: err.message, reintentar: () => { setErrorModal(null); cambiarFoto(file); } });
    } finally { setBusy(false); }
  };
  const guardarPrefs = async (patch) => {
    const nuevas = { ...prefs, ...patch };
    if (onActualizarAgente) onActualizarAgente({ preferencias: nuevas }); // al instante
    try { await guardarPreferencias(agente.id, nuevas); }
    catch (err) { setErrorModal({ titulo: 'No se guardó la preferencia', mensaje: 'Se aplicó en esta sesión, pero no quedó guardada.', detalle: err.message, reintentar: () => { setErrorModal(null); guardarPrefs(patch); } }); }
  };
  const cambiarTema = (v) => { onCambiarTema(v); guardarPrefs({ tema: v === 'light' ? 'claro' : 'oscuro' }); };

  const tarjeta = { background: 'var(--au-glass-fill)', border: '1px solid var(--au-glass-border)', borderRadius: RADIO_TARJETA, boxShadow: 'var(--au-shadow-card)', padding: 18, marginBottom: 14 };
  const titulo = { fontSize: 14, fontWeight: 600, color: C.muted, marginBottom: 10 };
  const chip = (activo, color) => ({ height: 36, boxSizing: 'border-box', padding: '0 14px', borderRadius: RADIO, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--au-font-text)', border: `1px solid ${activo ? 'var(--eq-borde)' : C.border}`, background: activo ? (color ? `linear-gradient(120deg, ${color}, ${color}cc)` : 'var(--eq-tinte)') : 'transparent', color: activo ? (color ? '#fff' : 'var(--eq-acento-texto)') : C.text, display: 'inline-flex', alignItems: 'center', gap: 8 });

  return (
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <ModalError C={C} error={errorModal} onCerrar={() => setErrorModal(null)} />
      <div style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: '0 0 12px' }}>Mi panel</div>
      {agente.ficha_completa_en && Date.now() - new Date(agente.ficha_completa_en).getTime() < 5 * 60 * 1000 && (
        <div className="au-banner au-banner--success" style={{ fontSize: 14, marginBottom: 12 }}><Ic n="CircleCheck" chico /> Ficha enviada. Gracias, {agente.nombre}: ya puedes seguir vendiendo; aquí queda por si hay que corregir algo.</div>
      )}

      <div style={{ ...tarjeta, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ width: 72, height: 72, borderRadius: RADIO_AVATAR, overflow: 'hidden', background: 'var(--equipo-grad)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: 30, flex: '0 0 72px' }}>
          {foto ? <img src={foto} alt="Tu foto de perfil" onError={() => setFoto(null)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (agente.nombre || 'A').split(' ').map((x) => x[0]).slice(0, 2).join('').toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{agente.nombre}</div>
          <div style={{ fontSize: 12, color: C.muted }}>{agente.tipo_agente === 'top_tours' ? 'Top Tours' : 'Sala de Ventas'}</div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" capture="user" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) cambiarFoto(f); e.target.value = ''; }} />
        <button type="button" className="au-btn au-btn--secondary" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()} style={{ height: 36, boxSizing: 'border-box', padding: '0 12px', fontSize: 12 }}>{busy ? 'Subiendo…' : <><Ic n="Camera" chico /> Cambiar foto</>}</button>
      </div>

      <div style={tarjeta}>
        <div style={titulo}>Tema</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['light', 'Claro'], ['dark', 'Oscuro']].map(([v, l]) => (
            <button key={v} type="button" aria-pressed={tema === v} style={chip(tema === v)} onClick={() => cambiarTema(v)}>{l}</button>
          ))}
        </div>
      </div>

      <div style={tarjeta}>
        <div style={titulo}>Menú</div>
        {/* APP 2/09 (F8): A · Arriba (pestañas con íconos) o B · Lateral (columna con grupos; en móvil, hamburguesa). Se guarda en agentes.preferencias.menu. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {MENUS.map((m) => (
            <button key={m} type="button" aria-pressed={menu === m} style={chip(menu === m)} onClick={() => guardarPrefs({ menu: m })}>
              <IconoMenu name={m === 'lateral' ? 'chevrons' : 'menu'} size={16} />{m === 'lateral' ? 'B · Lateral' : 'A · Arriba'}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>{menu === 'lateral' ? 'Columna a la izquierda con grupos (Ventas · Dinero · Referencia · Mi cuenta); se puede contraer. En el teléfono es el botón de menú.' : 'Pestañas horizontales con íconos; en el teléfono se deslizan.'} Las secciones son las mismas. · Menú {etiquetaMenu(menu).toLowerCase()}</div>
      </div>

      {agente.ficha_completa_en
        ? <FichaLectura C={C} agente={agente} />
        : (
          <div style={tarjeta}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>Mi ficha</div>
            <div style={{ fontSize: 12, color: C.muted }}>{agente.ficha_solicitada_en ? 'Pendiente de completar' : 'Sin solicitar todavía'}</div>
          </div>
        )}
    </div>
  );
}
